import { useEffect, useState, useCallback } from "react";
import { BarChart3, TrendingUp, Euro, Loader2, Save, Users, Target, Download, Filter, RefreshCw } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { toast } from "sonner";
import { adminApi } from "../api";

const MESES_ABR = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
const LINE_COLORS = ["#0F1829", "#E040FB", "#16A34A", "#EA580C", "#0EA5E9", "#CA8A04",
  "#DC2626", "#7C3AED", "#059669", "#DB2777", "#2563EB", "#D97706"];

const MESES = ["Ano inteiro", "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

const eur = (v) => (Number(v) || 0).toLocaleString("pt-PT", { style: "currency", currency: "EUR" });
const roaFmt = (v) => (v == null ? "—" : `${Number(v).toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}×`);
const pctFmt = (v) => (v == null ? "—" : `${(Number(v) * 100).toLocaleString("pt-PT", { maximumFractionDigits: 1 })}%`);

function Kpi({ icon: Icon, label, value, sub, tone = "dark", testid }) {
  const tones = {
    dark: "bg-[#0F1829] text-white", green: "bg-emerald-600 text-white",
    amber: "bg-amber-500 text-white", light: "bg-white text-[#0F1829] border border-[#E6E1D6]",
  };
  return (
    <div className={`rounded-xl p-4 ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs opacity-80 mb-1"><Icon size={14} /> {label}</div>
      <div className="text-2xl font-bold leading-tight">{value}</div>
      {sub && <div className="text-xs opacity-75 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function AdminMarketing() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});      // custos por campanha em edição
  const [savingKey, setSavingKey] = useState(null);
  const [gen, setGen] = useState({ fee_mensal: 0, comunicacao_pr: 0, designer: 0, outros_custos: 0, paid_media_adicional: 0 });
  const [savingGen, setSavingGen] = useState(false);
  const [evo, setEvo] = useState(null);
  const [compareYear, setCompareYear] = useState(0);
  const [evo2, setEvo2] = useState(null);
  const [hiddenCamps, setHiddenCamps] = useState(() => new Set());
  const [meta, setMeta] = useState(null);
  const [metaSyncing, setMetaSyncing] = useState(false);

  const isMonth = month > 0;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await adminApi.marketingEvaluation(year, month);
      setData(d);
      const dr = {};
      d.campanhas.forEach((c) => {
        dr[c.campanha] = { custo_ads: c.custo_ads, fee_paid_media: c.fee_paid_media, outros_custos: c.outros_custos };
      });
      setDrafts(dr);
      setGen({
        fee_mensal: d.custos_marketing.FeeMensal || 0,
        comunicacao_pr: d.custos_marketing.ComunicacaoPR || 0,
        designer: d.custos_marketing.Designer || 0,
        outros_custos: d.custos_marketing.OutrosCustos || 0,
        paid_media_adicional: d.custos_marketing.PaidMediaAdicional || 0,
      });
    } catch (e) {
      toast.error(e.message || "Erro", { description: "Avaliação de Marketing" });
    } finally { setLoading(false); }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const loadMeta = useCallback(async () => {
    try { setMeta(await adminApi.marketingMetaCampaigns(year, month)); }
    catch (e) { /* silencioso */ }
  }, [year, month]);
  useEffect(() => { loadMeta(); }, [loadMeta]);

  const mapMeta = async (campaign_id, campanha_dashboard) => {
    setMeta((m) => ({ ...m, campanhas: m.campanhas.map((c) => c.campaign_id === campaign_id ? { ...c, mapeado: campanha_dashboard || null, mapeado_ano: true } : c) }));
    try {
      await adminApi.marketingMetaMap({ campaign_id, campanha_dashboard, ano: year });
      await load();
    } catch (e) { toast.error(e.message || "Erro ao mapear"); await loadMeta(); }
  };

  const syncMeta = async () => {
    setMetaSyncing(true);
    try {
      const r = await adminApi.marketingMetaSync(3);
      toast.success("Gasto Meta atualizado.", { description: `${r.campanhas} campanhas` });
      await loadMeta(); await load(); await loadEvo();
    } catch (e) { toast.error(e.message || "Falha a atualizar Meta"); }
    finally { setMetaSyncing(false); }
  };

  const loadEvo = useCallback(async () => {
    try { setEvo(await adminApi.marketingEvolution(year)); }
    catch (e) { /* silencioso */ }
  }, [year]);
  useEffect(() => { loadEvo(); }, [loadEvo]);

  useEffect(() => {
    if (!compareYear) { setEvo2(null); return; }
    adminApi.marketingEvolution(compareYear).then(setEvo2).catch(() => setEvo2(null));
  }, [compareYear]);

  const exportXlsx = () => {
    fetch(adminApi.marketingExportUrl(year, month), { credentials: "include" })
      .then((r) => r.blob())
      .then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url; a.download = `avaliacao-marketing-${month ? month + "-" : ""}${year}.xlsx`;
        a.click(); URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Falha ao exportar."));
  };

  const toggleCamp = (name) =>
    setHiddenCamps((s) => { const n = new Set(s); n.has(name) ? n.delete(name) : n.add(name); return n; });

  const setDraft = (camp, field, val) =>
    setDrafts((d) => ({ ...d, [camp]: { ...d[camp], [field]: val } }));

  const saveCampaign = async (camp) => {
    if (!isMonth) { toast.info("Selecione um mês para registar custos por campanha."); return; }
    setSavingKey(camp);
    try {
      const dr = drafts[camp] || {};
      await adminApi.marketingSaveCampaignCost({
        campanha: camp, year, month,
        custo_ads: Number(dr.custo_ads) || 0,
        fee_paid_media: Number(dr.fee_paid_media) || 0,
        outros_custos: Number(dr.outros_custos) || 0,
      });
      toast.success("Custo guardado.", { description: camp });
      await load(); await loadEvo();
    } catch (e) { toast.error(e.message || "Erro"); }
    finally { setSavingKey(null); }
  };

  const saveGeneral = async () => {
    if (!isMonth) { toast.info("Selecione um mês para registar custos gerais."); return; }
    setSavingGen(true);
    try {
      await adminApi.marketingSaveMonthlyCost({
        year, month,
        fee_mensal: Number(gen.fee_mensal) || 0,
        comunicacao_pr: Number(gen.comunicacao_pr) || 0,
        designer: Number(gen.designer) || 0,
        outros_custos: Number(gen.outros_custos) || 0,
        paid_media_adicional: Number(gen.paid_media_adicional) || 0,
      });
      toast.success("Custos gerais de marketing guardados.");
      await load(); await loadEvo();
    } catch (e) { toast.error(e.message || "Erro"); }
    finally { setSavingGen(false); }
  };

  const t = data?.totais;
  const totalChart = MESES_ABR.map((label, i) => {
    const p = (evo?.total || [])[i] || {};
    const row = { mes: label, ROA: p.roa ?? null, ROI: p.roi == null ? null : +(p.roi * 100).toFixed(1) };
    if (evo2) {
      const p2 = (evo2.total || [])[i] || {};
      row[`ROA ${compareYear}`] = p2.roa ?? null;
      row[`ROI ${compareYear}`] = p2.roi == null ? null : +(p2.roi * 100).toFixed(1);
    }
    return row;
  });
  const visibleCamps = (evo?.campanhas || []).filter((c) => !hiddenCamps.has(c.campanha));
  const campChart = MESES_ABR.map((label, i) => {
    const row = { mes: label };
    visibleCamps.forEach((c) => { row[c.campanha] = c.pontos[i] ? c.pontos[i].roa : null; });
    return row;
  });
  const years = [];
  for (let y = now.getFullYear(); y >= 2023; y--) years.push(y);

  return (
    <div data-testid="admin-marketing" className="max-w-6xl">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-11 h-11 grid place-items-center rounded-xl bg-[#0F1829] text-white"><BarChart3 size={22} /></div>
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Avaliação de Marketing</h1>
          <p className="text-sm text-[#4B5563]">ROA por campanha e ROI global de marketing (receita de encomendas pagas por campanha vs custos).</p>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2 my-4">
        <select value={year} onChange={(e) => setYear(Number(e.target.value))}
          data-testid="mkt-year" className="px-3 py-2 border border-[#E6E1D6] rounded text-sm bg-white">
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <select value={month} onChange={(e) => setMonth(Number(e.target.value))}
          data-testid="mkt-month" className="px-3 py-2 border border-[#E6E1D6] rounded text-sm bg-white">
          {MESES.map((m, i) => <option key={i} value={i}>{m}</option>)}
        </select>
        <span className="text-xs text-[#6B7280]">comparar com:</span>
        <select value={compareYear} onChange={(e) => setCompareYear(Number(e.target.value))}
          data-testid="mkt-compare-year" className="px-3 py-2 border border-[#E6E1D6] rounded text-sm bg-white">
          <option value={0}>—</option>
          {years.filter((y) => y !== year).map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <button onClick={exportXlsx} data-testid="mkt-export"
          className="ml-auto inline-flex items-center gap-2 px-3 py-2 border border-[#0F1829] text-[#0F1829] text-sm rounded hover:bg-[#F5F2EB]">
          <Download size={14} /> Exportar Excel
        </button>
        {loading && <Loader2 size={16} className="animate-spin text-[#978D78]" />}
      </div>

      {/* KPIs */}
      {t && (
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-6">
          <Kpi icon={Euro} label="Receita campanhas" value={eur(t.receita)} sub="Atribuída a leads" tone="dark" testid="kpi-receita" />
          <Kpi icon={Euro} label="Receita total" value={eur(t.receita_total)} sub="Todas as encomendas pagas" tone="dark" testid="kpi-receita-total" />
          <Kpi icon={Target} label="Custo Ads/Campanhas" value={eur(t.custo_ads)} tone="light" testid="kpi-custo-ads" />
          <Kpi icon={TrendingUp} label="ROA total" value={roaFmt(t.roa)} sub="Receita campanhas ÷ ads" tone="green" testid="kpi-roa" />
          <Kpi icon={Euro} label="Custos gerais mkt" value={eur(t.custos_gerais)} sub="Fee + PR + designer + paid media" tone="light" testid="kpi-gerais" />
          <Kpi icon={TrendingUp} label="ROI Marketing" value={pctFmt(t.roi)} sub={`Receita total vs custo ${eur(t.custo_total_marketing)}`} tone="amber" testid="kpi-roi" />
        </div>
      )}

      {!isMonth && (
        <div className="mb-4 px-3 py-2 text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded">
          A ver o <strong>ano inteiro</strong> (custos somados). Para <strong>registar/editar custos</strong>, selecione um mês.
        </div>
      )}

      {/* Gráficos de evolução mensal */}
      {evo && (
        <div className="grid lg:grid-cols-2 gap-4 mb-6">
          <div className="bg-white border border-[#E6E1D6] rounded-md p-4" data-testid="chart-roa-roi-total">
            <h3 className="text-sm font-semibold mb-3">ROA & ROI mensais (todas as campanhas) — {year}</h3>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={totalChart} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EFEBE1" />
                <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="l" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11 }} unit="%" />
                <Tooltip formatter={(v, n) => (n === "ROI" ? `${v}%` : `${v}×`)} />
                <Legend />
                <Line yAxisId="l" type="monotone" dataKey="ROA" stroke="#16A34A" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                <Line yAxisId="r" type="monotone" dataKey="ROI" stroke="#E040FB" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                {evo2 && <Line yAxisId="l" type="monotone" dataKey={`ROA ${compareYear}`} stroke="#16A34A" strokeDasharray="5 4" strokeWidth={2} dot={false} connectNulls />}
                {evo2 && <Line yAxisId="r" type="monotone" dataKey={`ROI ${compareYear}`} stroke="#E040FB" strokeDasharray="5 4" strokeWidth={2} dot={false} connectNulls />}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white border border-[#E6E1D6] rounded-md p-4" data-testid="chart-roa-campaigns">
            <h3 className="text-sm font-semibold mb-3">ROA mensal por campanha — {year}</h3>
            {(evo.campanhas || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2 max-h-20 overflow-y-auto" data-testid="mkt-camp-filter">
                <span className="text-[11px] text-[#6B7280] inline-flex items-center gap-1 mr-1"><Filter size={11} /> Mostrar:</span>
                {(evo.campanhas || []).map((c, i) => {
                  const on = !hiddenCamps.has(c.campanha);
                  return (
                    <button key={c.campanha} onClick={() => toggleCamp(c.campanha)}
                      className={`text-[10px] px-2 py-0.5 rounded-full border ${on ? "text-white" : "text-[#9CA3AF] bg-white"}`}
                      style={on ? { background: LINE_COLORS[i % LINE_COLORS.length], borderColor: LINE_COLORS[i % LINE_COLORS.length] } : { borderColor: "#E6E1D6" }}
                      title={c.campanha}>
                      {c.campanha.length > 22 ? c.campanha.slice(0, 22) + "…" : c.campanha}
                    </button>
                  );
                })}
              </div>
            )}
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={campChart} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EFEBE1" />
                <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => `${v}×`} />
                {visibleCamps.map((c) => {
                  const idx = (evo.campanhas || []).findIndex((x) => x.campanha === c.campanha);
                  return (
                    <Line key={c.campanha} type="monotone" dataKey={c.campanha}
                      stroke={LINE_COLORS[idx % LINE_COLORS.length]} strokeWidth={1.5} dot={false} connectNulls />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
            <p className="text-[11px] text-[#6B7280] mt-1">O ROA só aparece nos meses com custos registados para a campanha.</p>
          </div>
        </div>
      )}

      {/* Gasto de anúncios Meta (Facebook/Instagram) */}
      <div className="bg-white border border-[#E6E1D6] rounded-md p-4 mb-6" data-testid="meta-ads-section">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div>
            <h3 className="text-sm font-semibold">Custos de anúncios Meta (Facebook/Instagram)</h3>
            <p className="text-[11px] text-[#6B7280]">
              Gasto real por campanha ({isMonth ? MESES[month] : "ano inteiro"} {year}). O mapeamento é <strong>por ano</strong> — ligar cada campanha à campanha/landing desse ano;
              engagement/sem formulário → "Outros custos de ads".
              {meta?.atualizado_em && <> · Atualizado {new Date(meta.atualizado_em).toLocaleString("pt-PT")}</>}
            </p>
          </div>
          <button onClick={syncMeta} disabled={metaSyncing} data-testid="meta-sync-btn"
            className="inline-flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-md bg-[#0F1829] text-white disabled:opacity-60">
            {metaSyncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Atualizar agora
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF8F2] text-[#6B7280] text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-3 py-2">Campanha Meta</th>
                <th className="text-right px-3 py-2">Gasto</th>
                <th className="text-left px-3 py-2">Atribuir a</th>
              </tr>
            </thead>
            <tbody>
              {(meta?.campanhas || []).length === 0 && (
                <tr><td colSpan={3} className="px-3 py-4 text-center text-[#9CA3AF] text-xs">
                  Sem gasto Meta neste período. Clica "Atualizar agora" para sincronizar.
                </td></tr>
              )}
              {(meta?.campanhas || []).map((c) => (
                <tr key={c.campaign_id} className="border-t border-[#F0ECE2]" data-testid={`meta-camp-${c.campaign_id}`}>
                  <td className="px-3 py-2 font-medium text-[#0F1829]" title={c.campaign_name}>
                    {c.campaign_name || c.campaign_id}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{eur(c.spend)}</td>
                  <td className="px-3 py-2">
                    <select
                      data-testid={`meta-map-${c.campaign_id}`}
                      value={c.mapeado || ""}
                      onChange={(e) => mapMeta(c.campaign_id, e.target.value)}
                      className={`text-xs border rounded-md px-2 py-1 w-full max-w-[320px] ${c.mapeado ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50"}`}>
                      <option value="">— Pendente (por classificar) —</option>
                      <option value="__OUTROS__">Outros custos de ads (sem formulário)</option>
                      <optgroup label="Campanhas / landing">
                        {(meta?.alvos || []).map((a) => <option key={a} value={a}>{a}</option>)}
                      </optgroup>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tabela de campanhas */}
      <div className="bg-white border border-[#E6E1D6] rounded-md overflow-x-auto mb-6">
        <table className="w-full text-sm">
          <thead className="bg-[#FAF8F2] text-[#6B7280] text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-3 py-2">Campanha</th>
              <th className="text-right px-3 py-2">Leads</th>
              <th className="text-right px-3 py-2">Clientes</th>
              <th className="text-right px-3 py-2">Compr.</th>
              <th className="text-right px-3 py-2">Receita</th>
              <th className="text-right px-3 py-2">Custo Ads</th>
              <th className="text-right px-3 py-2">Fee Media</th>
              <th className="text-right px-3 py-2">Outros</th>
              <th className="text-right px-3 py-2">ROA</th>
              {isMonth && <th className="px-3 py-2"></th>}
            </tr>
          </thead>
          <tbody>
            {(data?.campanhas || []).map((c) => {
              const dr = drafts[c.campanha] || {};
              return (
                <tr key={c.campanha} className="border-t border-[#EFEBE1]" data-testid={`mkt-row-${c.campanha}`}>
                  <td className="px-3 py-2 font-medium max-w-[240px] truncate" title={c.campanha}>
                    <span className="align-middle">{c.campanha}</span>
                    {c.fonte_ads === "meta" ? (
                      <span data-testid={`fonte-${c.campanha}`} className="ml-2 inline-block align-middle text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700" title="Custo de anúncios vindo do Meta (automático)">Meta</span>
                    ) : (c.custo_ads > 0 && (
                      <span data-testid={`fonte-${c.campanha}`} className="ml-2 inline-block align-middle text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600" title="Custo de anúncios inserido manualmente">manual</span>
                    ))}
                  </td>
                  <td className="px-3 py-2 text-right">{c.leads}</td>
                  <td className="px-3 py-2 text-right">{c.clientes}</td>
                  <td className="px-3 py-2 text-right">{c.compradores}</td>
                  <td className="px-3 py-2 text-right font-semibold text-emerald-700">{eur(c.receita)}</td>
                  {isMonth ? (
                    <>
                      <td className="px-2 py-1 text-right">
                        <input type="number" step="0.01"
                          value={c.fonte_ads === "meta" ? c.custo_ads : (dr.custo_ads ?? 0)}
                          disabled={c.fonte_ads === "meta"}
                          title={c.fonte_ads === "meta" ? "Custo automático vindo do Meta (não editável)" : ""}
                          onChange={(e) => setDraft(c.campanha, "custo_ads", e.target.value)}
                          data-testid={`mkt-ads-${c.campanha}`}
                          className={`w-24 px-2 py-1 border border-[#E6E1D6] rounded text-right text-sm ${c.fonte_ads === "meta" ? "bg-blue-50 text-blue-700 cursor-not-allowed" : ""}`} />
                      </td>
                      <td className="px-2 py-1 text-right">
                        <input type="number" step="0.01" value={dr.fee_paid_media ?? 0}
                          onChange={(e) => setDraft(c.campanha, "fee_paid_media", e.target.value)}
                          className="w-20 px-2 py-1 border border-[#E6E1D6] rounded text-right text-sm" />
                      </td>
                      <td className="px-2 py-1 text-right">
                        <input type="number" step="0.01" value={dr.outros_custos ?? 0}
                          onChange={(e) => setDraft(c.campanha, "outros_custos", e.target.value)}
                          className="w-20 px-2 py-1 border border-[#E6E1D6] rounded text-right text-sm" />
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-3 py-2 text-right">{eur(c.custo_ads)}</td>
                      <td className="px-3 py-2 text-right">{eur(c.fee_paid_media)}</td>
                      <td className="px-3 py-2 text-right">{eur(c.outros_custos)}</td>
                    </>
                  )}
                  <td className="px-3 py-2 text-right font-bold">{roaFmt(c.roa)}</td>
                  {isMonth && (
                    <td className="px-2 py-1">
                      <button onClick={() => saveCampaign(c.campanha)} disabled={savingKey === c.campanha}
                        data-testid={`mkt-save-${c.campanha}`}
                        className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded bg-[#0F1829] text-white hover:bg-black disabled:opacity-50">
                        {savingKey === c.campanha ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Guardar
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
            {(!data || data.campanhas.length === 0) && !loading && (
              <tr><td colSpan={isMonth ? 10 : 9} className="px-3 py-6 text-center text-[#6B7280]">Sem campanhas com leads neste período.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Custos gerais de marketing */}
      <div className="bg-white border border-[#E6E1D6] rounded-md p-5" data-testid="mkt-general-costs">
        <div className="flex items-center gap-2 mb-3">
          <Users size={16} className="text-[#0F1829]" />
          <h2 className="font-semibold">Custos gerais de marketing {isMonth ? `— ${MESES[month]} ${year}` : "(somados no ano)"}</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[["fee_mensal", "Fee mensal"], ["comunicacao_pr", "Comunicação / PR"], ["designer", "Designer"], ["paid_media_adicional", "Paid media adicional"], ["outros_custos", "Outros"]].map(([k, label]) => (
            <label key={k} className="text-sm">
              <span className="block text-xs text-[#6B7280] mb-1">{label}</span>
              <input type="number" step="0.01" value={gen[k]} disabled={!isMonth}
                onChange={(e) => setGen((g) => ({ ...g, [k]: e.target.value }))}
                data-testid={`mkt-gen-${k}`}
                className="w-full px-3 py-2 border border-[#E6E1D6] rounded text-right disabled:bg-[#F5F2EB]" />
            </label>
          ))}
        </div>
        {isMonth && (
          <div className="flex justify-end mt-4">
            <button onClick={saveGeneral} disabled={savingGen} data-testid="mkt-save-general"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#0F1829] text-white text-sm rounded hover:bg-black disabled:opacity-50">
              {savingGen ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Guardar custos gerais
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
